import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Alert,
  Box,
  Typography,
  Grid,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import {
  validarCompensacao,
  criarLancamentoDebito,
} from "../../../../services/api-conta-corrente.service";
import { formatCurrency } from "../../../../utils/formatValue";
import styles from "./styles.module.css";

const ModalLancarDebito = ({
  open,
  onClose,
  cnpj,
  dadosCliente,
  grupos,
  onSuccess,
}) => {
  const [tipoImposto, setTipoImposto] = useState("");
  const [mesReferencia, setMesReferencia] = useState("");
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [saldoGrupo, setSaldoGrupo] = useState(null);
  const [saldoInsuficiente, setSaldoInsuficiente] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const tiposImposto = {
    federal: [
      { value: "pis", label: "PIS" },
      { value: "cofins", label: "COFINS" },
      { value: "irpj", label: "IRPJ" },
      { value: "csll", label: "CSLL" },
      { value: "inss", label: "INSS" },
    ],
    estadual: [
      { value: "icms", label: "ICMS" },
      { value: "icms-st", label: "ICMS-ST" },
    ],
    municipal: [
      { value: "iss", label: "ISS" },
      { value: "iptu", label: "IPTU" },
    ],
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (tipoImposto) {
      const grupo = grupos.find((g) => {
        const tiposGrupo = tiposImposto[g.tipo] || [];
        return tiposGrupo.some((t) => t.value === tipoImposto);
      });
      if (grupo) {
        setSaldoGrupo(grupo.saldo);
      } else {
        // Grupo não encontrado = sem crédito disponível
        setSaldoGrupo(0);
      }
    } else {
      setSaldoGrupo(null);
    }
  }, [tipoImposto, grupos]);

  useEffect(() => {
    if (valor && saldoGrupo !== null) {
      const valorNumerico = parseFloat(
        valor.replace(/[^\d,]/g, "").replace(",", ".")
      );
      if (!isNaN(valorNumerico) && valorNumerico > saldoGrupo) {
        setSaldoInsuficiente(true);
      } else {
        setSaldoInsuficiente(false);
      }
    } else {
      setSaldoInsuficiente(false);
    }
  }, [valor, saldoGrupo]);

  const resetForm = () => {
    setTipoImposto("");
    setMesReferencia("");
    setDescricao("");
    setValor("");
    setSaldoGrupo(null);
    setSaldoInsuficiente(false);
    setErro("");
  };

  const formatarValor = (valor) => {
    const apenasNumeros = valor.replace(/\D/g, "");
    if (apenasNumeros === "") return "";

    const numero = parseFloat(apenasNumeros) / 100;

    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(numero);
  };

  const handleValorChange = (e) => {
    const valorFormatado = formatarValor(e.target.value);
    setValor(valorFormatado);
  };

  const getMaxDateLimit = () => {
    return "2025-12";
  };

  const handleSubmit = async () => {
    setErro("");

    if (!tipoImposto) {
      setErro("Tipo de imposto é obrigatório");
      return;
    }

    if (!mesReferencia) {
      setErro("Mês de referência é obrigatório");
      return;
    }

    if (!descricao.trim()) {
      setErro("Descrição é obrigatória");
      return;
    }

    if (!valor || valor === "R$ 0,00") {
      setErro("Valor deve ser maior que zero");
      return;
    }

    if (saldoInsuficiente) {
      setErro("Saldo insuficiente para realizar a operação");
      return;
    }

    const valorNumerico = parseFloat(
      valor.replace(/[^\d,]/g, "").replace(",", ".")
    );

    setLoading(true);
    try {
      const validacao = await validarCompensacao({
        cnpj,
        tipo_imposto: tipoImposto,
        valor: valorNumerico,
      });

      if (!validacao.permitido) {
        setErro(validacao.mensagem);
        setLoading(false);
        return;
      }

      await criarLancamentoDebito({
        cnpj,
        tipo_imposto: tipoImposto,
        descricao: descricao.trim(),
        mes_referencia: mesReferencia,
        valor: valorNumerico,
      });

      onSuccess();
    } catch (error) {
      console.error(error);
      setErro(
        error.response?.data?.mensagem ||
          "Ops, erro ao lançar débito. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const calcularSaldoTotal = () => {
    return grupos.reduce((sum, g) => sum + g.saldo, 0);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: styles.dialog_paper,
      }}
    >
      <DialogTitle className={styles.dialog_title}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="span" sx={{ fontSize: "18px", fontWeight: 600 }}>
            💳 Lançar Débito
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            className={styles.close_button}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={styles.dialog_content}>
        <Alert severity="info" className={styles.alert_info}>
          <strong>ℹ️ Informação:</strong> O débito será lançado na conta corrente do cliente e
          reduzirá o saldo disponível de créditos.
        </Alert>

        <Box className={styles.saldo_disponivel}>
          <Typography className={styles.saldo_disponivel_label}>
            Saldo Total Disponível:
          </Typography>
          <Typography className={styles.saldo_disponivel_value}>
            {formatCurrency(calcularSaldoTotal())}
          </Typography>
        </Box>

        <Box component="form">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box className={styles.form_group} sx={{ marginBottom: 0 }}>
                <Typography className={styles.form_label}>
                  Tipo de Imposto <span className={styles.required}>*</span>
                </Typography>
                <FormControl fullWidth required size="small">
                  <Select
                    value={tipoImposto}
                    onChange={(e) => setTipoImposto(e.target.value)}
                    className={styles.form_select}
                  >
                    <MenuItem disabled value="">
                      <em>Selecione o tipo</em>
                    </MenuItem>
                    <MenuItem disabled>
                      <em>🏛️ Impostos Federais</em>
                    </MenuItem>
                    {tiposImposto.federal.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </MenuItem>
                    ))}
                    <MenuItem disabled>
                      <em>🏢 Impostos Estaduais</em>
                    </MenuItem>
                    {tiposImposto.estadual.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </MenuItem>
                    ))}
                    <MenuItem disabled>
                      <em>🏙️ Impostos Municipais</em>
                    </MenuItem>
                    {tiposImposto.municipal.map((tipo) => (
                      <MenuItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box className={styles.form_group} sx={{ marginBottom: 0 }}>
                <Typography className={styles.form_label}>
                  Mês de Referência <span className={styles.required}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type="month"
                  value={mesReferencia}
                  onChange={(e) => setMesReferencia(e.target.value)}
                  inputProps={{
                    max: getMaxDateLimit(),
                  }}
                  required
                  size="small"
                  className={styles.form_input}
                />
              </Box>
            </Grid>

            {saldoGrupo !== null && (
              <Grid item xs={12}>
                {saldoGrupo > 0 ? (
                  <Box className={styles.saldo_disponivel} sx={{ background: "#e8f5e9", marginBottom: "20px", padding: "12px", borderRadius: "4px", border: "1px solid #4caf50" }}>
                    <Typography className={styles.saldo_disponivel_label} sx={{ color: "#2e7d32", fontWeight: 600 }}>
                      ✅ Crédito Disponível:
                    </Typography>
                    <Typography className={styles.saldo_disponivel_value} sx={{ color: "#2e7d32", fontWeight: 700, fontSize: "1.1rem" }}>
                      {formatCurrency(saldoGrupo)}
                    </Typography>
                  </Box>
                ) : (
                  <Box className={styles.saldo_disponivel} sx={{ background: "#fff3e0", marginBottom: "20px", padding: "12px", borderRadius: "4px", border: "1px solid #ff9800" }}>
                    <Typography className={styles.saldo_disponivel_label} sx={{ color: "#f57c00", fontWeight: 600 }}>
                      ⚠️ Crédito Disponível:
                    </Typography>
                    <Typography className={styles.saldo_disponivel_value} sx={{ color: "#f57c00", fontWeight: 700, fontSize: "1.1rem" }}>
                      {formatCurrency(0)}
                    </Typography>
                    <Typography sx={{ color: "#f57c00", fontSize: "0.75rem", marginTop: "4px" }}>
                      Não há crédito disponível para este tipo de imposto
                    </Typography>
                  </Box>
                )}
              </Grid>
            )}

            <Grid item xs={12}>
              <Box className={styles.form_group}>
                <Typography className={styles.form_label}>
                  Descrição <span className={styles.required}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Ex: PIS - Apuração 11/2024"
                  inputProps={{ maxLength: 255 }}
                  required
                  size="small"
                  className={styles.form_input}
                />
                <Typography className={styles.form_help}>
                  Descreva o lançamento (máximo 255 caracteres)
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box className={styles.form_group}>
                <Typography className={styles.form_label}>
                  Valor <span className={styles.required}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  value={valor}
                  onChange={handleValorChange}
                  placeholder="R$ 0,00"
                  required
                  error={saldoInsuficiente}
                  size="small"
                  className={styles.form_input}
                />
                <Typography className={styles.form_help}>
                  Digite o valor da compensação a ser realizada
                </Typography>
              </Box>
            </Grid>

            {saldoInsuficiente && (
              <Grid item xs={12}>
                <Alert severity="warning" className={styles.alert_warning}>
                  <strong>⚠️ Atenção:</strong> O valor informado é maior que o saldo disponível
                  para este tipo de imposto. A operação não poderá ser concluída.
                </Alert>
              </Grid>
            )}

            {erro && (
              <Grid item xs={12}>
                <Alert severity="error">{erro}</Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions className={styles.dialog_actions}>
        <Button onClick={onClose} className={styles.btn_cancelar}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className={styles.btn_confirmar}
          disabled={loading || saldoInsuficiente || !tipoImposto || !mesReferencia || !descricao || !valor || (saldoGrupo !== null && saldoGrupo === 0)}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : "Confirmar Lançamento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalLancarDebito;
